import { CalendarClock, Eraser, Save, Send, Timer, Wand2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonBuilder } from "../components/ButtonBuilder";
import { CardPreview } from "../components/CardPreview";
import { ChannelSelect } from "../components/ChannelSelect";
import { EmojiBar } from "../components/EmojiBar";
import { MediaUploader } from "../components/MediaUploader";
import { ApiError, api } from "../lib/api";
import { canSendCards, useAuth } from "../lib/auth";
import { cropImageFile } from "../lib/mediaCrop";
import { createMediaPreview } from "../lib/mediaPreview";
import type { CardPayload, Channel, CropPosition, MediaAspectRatio, MediaType } from "../lib/types";
import { normalizeUrl, validateButtons, validateCardPayload } from "../lib/validators";

const initialPayload: CardPayload = {
  channelId: "",
  mediaType: "photo",
  title: "",
  caption: "",
  buttons: [],
  buttonLayout: 2
};

const CURRENT_CARD_KEY = "telegram-card-current-payload";
type SendMode = "now" | "schedule";

function resolveTargetId(payload: CardPayload) {
  return payload.targetId ?? payload.channelId;
}

function getTargetKind(channel: Channel) {
  return channel.type ?? channel.targetType ?? "channel";
}

function toDatetimeLocalValue(date: Date) {
  return new Date(date.getTime() + 7 * 60 * 60 * 1000).toISOString().slice(0, 16);
}

function defaultScheduleTime() {
  return toDatetimeLocalValue(new Date(Date.now() + 10 * 60 * 1000));
}

function thailandLocalToUtcIso(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match.map(Number);
  return new Date(Date.UTC(year, month - 1, day, hour - 7, minute)).toISOString();
}

export function CreateCard() {
  const { user } = useAuth();
  const [payload, setPayload] = useState<CardPayload>(initialPayload);
  const [media, setMedia] = useState<File | null>(null);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [mediaAspectRatio, setMediaAspectRatio] = useState<MediaAspectRatio>("original");
  const [cropPosition, setCropPosition] = useState<CropPosition>("center");
  const [sendMode, setSendMode] = useState<SendMode>("now");
  const [scheduledAtLocal, setScheduledAtLocal] = useState(defaultScheduleTime);
  const captionRef = useRef<HTMLTextAreaElement | null>(null);

  const selectedChannel = useMemo(
    () => channels.find((channel) => channel.id === resolveTargetId(payload)),
    [channels, payload.channelId, payload.targetId]
  );

  const loadChannels = useCallback(async () => {
    setLoadingChannels(true);
    try {
      const result = await api.channels();
      setChannels(result.items);
      setPayload((current) => {
        if (resolveTargetId(current)) return current;
        const defaultChannel = result.items.find((channel) => channel.active && channel.isDefault) ?? result.items.find((channel) => channel.active);
        return defaultChannel
          ? { ...current, channelId: defaultChannel.id, target: getTargetKind(defaultChannel), targetId: defaultChannel.id, targetType: getTargetKind(defaultChannel) }
          : current;
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "โหลด channel ไม่สำเร็จ");
    } finally {
      setLoadingChannels(false);
    }
  }, []);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  useEffect(() => {
    const stored = localStorage.getItem("telegram-card-edit-payload");
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored) as Partial<CardPayload>;
      setPayload({
        ...initialPayload,
        ...parsed,
        target: parsed.target ?? parsed.targetType,
        targetId: parsed.targetId ?? parsed.channelId,
        targetType: parsed.targetType
      });
      setNotice("โหลด payload จาก Draft/Template แล้ว");
    } catch {
      setError("payload ที่โหลดมาไม่ถูกต้อง");
    } finally {
      localStorage.removeItem("telegram-card-edit-payload");
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(CURRENT_CARD_KEY, JSON.stringify(payload));
  }, [payload]);

  const updatePayload = (patch: Partial<CardPayload>) => setPayload((current) => ({ ...current, ...patch }));
  const updateTarget = (channelId: string) => {
    const selected = channels.find((channel) => channel.id === channelId);
    updatePayload({
      channelId,
      target: selected ? getTargetKind(selected) : undefined,
      targetId: channelId,
      targetType: selected ? getTargetKind(selected) : undefined
    });
  };

  const cleanPayload = () => ({
    ...payload,
    buttons: payload.buttons
      .filter((button) => button.text.trim() && button.url.trim())
      .map((button) => ({ ...button, text: button.text.trim(), url: normalizeUrl(button.url) }))
  });

  const submitPayload = async (submittedMedia: File) => {
    const target = selectedChannel ? getTargetKind(selectedChannel) : payload.target ?? payload.targetType ?? "channel";
    const selectedTargetId = selectedChannel?.id ?? payload.targetId ?? payload.channelId;
    const cleaned = cleanPayload();
    return {
      ...cleaned,
      channelId: selectedTargetId,
      target,
      targetId: selectedTargetId,
      targetType: target,
      mediaPreview: await createMediaPreview(submittedMedia, payload.mediaType)
    };
  };

  const insertCaptionText = (snippet: string) => {
    const textarea = captionRef.current;
    if (!textarea) {
      updatePayload({ caption: `${payload.caption}${snippet}` });
      return;
    }

    const start = textarea.selectionStart ?? payload.caption.length;
    const end = textarea.selectionEnd ?? start;
    const nextCaption = `${payload.caption.slice(0, start)}${snippet}${payload.caption.slice(end)}`;
    updatePayload({ caption: nextCaption });

    window.requestAnimationFrame(() => {
      textarea.focus();
      const nextPosition = start + snippet.length;
      textarea.setSelectionRange(nextPosition, nextPosition);
    });
  };

  const mediaToSubmit = async () =>
    payload.mediaType === "photo" && mediaAspectRatio !== "original"
      ? await cropImageFile(media!, mediaAspectRatio, cropPosition)
      : media!;

  const sendNow = async () => {
    if (!canSendCards(user)) {
      setError("role นี้ไม่มีสิทธิ์ส่งโพสต์");
      return;
    }
    const validationError = validateCardPayload(payload, media);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setNotice("");
    setError("");
    try {
      const submittedMedia = await mediaToSubmit();
      const result = await api.sendNow(await submitPayload(submittedMedia), submittedMedia);
      setNotice(`ส่งสำเร็จแล้ว${result.history.telegramMessageId ? ` (message id: ${result.history.telegramMessageId})` : ""}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ส่ง Telegram ไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  };

  const schedulePost = async () => {
    if (!canSendCards(user)) {
      setError("role นี้ไม่มีสิทธิ์ตั้งเวลาโพสต์");
      return;
    }
    const validationError = validateCardPayload(payload, media);
    if (validationError) {
      setError(validationError);
      return;
    }
    const scheduledAtIso = thailandLocalToUtcIso(scheduledAtLocal);
    const scheduledDate = scheduledAtIso ? new Date(scheduledAtIso) : null;
    if (!scheduledDate || Number.isNaN(scheduledDate.getTime()) || scheduledDate.getTime() <= Date.now()) {
      setError("กรุณาเลือกเวลาตั้งโพสต์ในอนาคต");
      return;
    }
    setSaving(true);
    setNotice("");
    setError("");
    try {
      const submittedMedia = await mediaToSubmit();
      const result = await api.scheduleCard(await submitPayload(submittedMedia), submittedMedia, scheduledAtIso!);
      setNotice(`บันทึก Schedule แล้ว (${new Date(result.scheduled.scheduledAt).toLocaleString("th-TH")})`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "บันทึก Schedule ไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async () => {
    const buttonError = validateButtons(payload.buttons);
    if (buttonError) {
      setError(buttonError);
      return;
    }
    const name = window.prompt("ชื่อ Draft", payload.title || "Untitled draft");
    if (!name) return;
    setSaving(true);
    setError("");
    try {
      await api.saveDraft({ name, payload: cleanPayload() });
      setNotice("บันทึก Draft แล้ว");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "บันทึก Draft ไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  };

  const saveTemplate = async () => {
    const buttonError = validateButtons(payload.buttons);
    if (buttonError) {
      setError(buttonError);
      return;
    }
    const name = window.prompt("ชื่อ Template", payload.title || "Untitled template");
    if (!name) return;
    setSaving(true);
    setError("");
    try {
      await api.saveTemplate({ name, payload: cleanPayload() });
      setNotice("บันทึก Template แล้ว");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "บันทึก Template ไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_440px]">
      <section className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-ink">Create Telegram Card</h1>
            <p className="mt-1 text-sm text-slate-500">
              ส่งไปยัง{" "}
              {selectedChannel
                ? `[${(selectedChannel.type ?? selectedChannel.targetType ?? "channel") === "group" ? "Group" : "Channel"}] ${selectedChannel.name} (${selectedChannel.telegramId})`
                : "Telegram Channel / Group"}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={saveDraft}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded border border-line bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
            >
              <Save size={16} />
              Draft
            </button>
            <button
              type="button"
              onClick={saveTemplate}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded border border-line bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
            >
              <Wand2 size={16} />
              Template
            </button>
            <button
              type="button"
              onClick={() => setSendMode("now")}
              className={`inline-flex items-center gap-2 rounded border px-3 py-2 text-sm font-semibold ${
                sendMode === "now" ? "border-brand bg-brand text-white" : "border-line bg-white text-slate-700 hover:bg-slate-100"
              }`}
            >
              <Send size={16} />
              Send Now
            </button>
            <button
              type="button"
              onClick={() => setSendMode("schedule")}
              className={`inline-flex items-center gap-2 rounded border px-3 py-2 text-sm font-semibold ${
                sendMode === "schedule" ? "border-brand bg-brand text-white" : "border-line bg-white text-slate-700 hover:bg-slate-100"
              }`}
            >
              <Timer size={16} />
              Schedule
            </button>
            <button
              type="button"
              onClick={sendMode === "now" ? sendNow : schedulePost}
              disabled={saving || !canSendCards(user)}
              className="inline-flex items-center gap-2 rounded bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sendMode === "now" ? <Send size={16} /> : <CalendarClock size={16} />}
              {saving ? "Saving..." : sendMode === "now" ? "Send Now" : "Save Schedule"}
            </button>
          </div>
        </div>

        {notice ? <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{notice}</div> : null}
        {error ? <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}

        {sendMode === "schedule" ? (
          <div className="rounded border border-line bg-white p-4 shadow-sm">
            <label className="block max-w-sm">
              <span className="mb-1 block text-sm font-medium text-slate-700">Schedule time (Thailand)</span>
              <input
                type="datetime-local"
                value={scheduledAtLocal}
                min={toDatetimeLocalValue(new Date(Date.now() + 60 * 1000))}
                onChange={(event) => setScheduledAtLocal(event.target.value)}
                className="focus-ring w-full rounded border border-line px-3 py-2"
              />
            </label>
          </div>
        ) : null}

        <div className="grid gap-4 rounded border border-line bg-white p-4 shadow-sm md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Target</span>
            <ChannelSelect channels={channels} value={resolveTargetId(payload)} onChange={updateTarget} onRefresh={loadChannels} />
            {loadingChannels ? <span className="mt-1 block text-xs text-slate-500">Loading channels...</span> : null}
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Media type</span>
            <select
              value={payload.mediaType}
              onChange={(event) => {
                updatePayload({ mediaType: event.target.value as MediaType });
                setMedia(null);
                setMediaAspectRatio("original");
                setCropPosition("center");
              }}
              className="focus-ring w-full rounded border border-line bg-white px-3 py-2 text-sm"
            >
              <option value="photo">Photo</option>
              <option value="animation">GIF / Animation</option>
              <option value="video">MP4 Video</option>
            </select>
          </label>

          <div className="md:col-span-2">
            <MediaUploader
              mediaType={payload.mediaType}
              file={media}
              aspectRatio={mediaAspectRatio}
              cropPosition={cropPosition}
              onFileChange={setMedia}
              onAspectRatioChange={setMediaAspectRatio}
              onCropPositionChange={setCropPosition}
            />
          </div>
        </div>

        <div className="space-y-4 rounded border border-line bg-white p-4 shadow-sm">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Title</span>
            <input
              value={payload.title}
              onChange={(event) => updatePayload({ title: event.target.value })}
              className="focus-ring w-full rounded border border-line px-3 py-2"
              placeholder="หัวข้อภายในทีม หรือบรรทัดแรกใน preview"
            />
          </label>

          <div className="flex items-center justify-between gap-3">
            <span className="block text-sm font-medium text-slate-700">Caption</span>
            <button
              type="button"
              onClick={() => {
                updatePayload({ caption: "" });
                window.requestAnimationFrame(() => captionRef.current?.focus());
              }}
              className="inline-flex items-center gap-2 rounded border border-line bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100"
            >
              <Eraser size={14} />
              Clear Caption
            </button>
          </div>

          <label className="block">
            <textarea
              ref={captionRef}
              value={payload.caption}
              onChange={(event) => updatePayload({ caption: event.target.value })}
              className="focus-ring min-h-44 w-full resize-y rounded border border-line px-3 py-2 leading-6"
              placeholder="พิมพ์ caption ที่จะส่งเข้า Telegram"
              maxLength={1024}
            />
          </label>

          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-slate-500">
              {payload.caption.length ? `${payload.caption.length}/1024` : "Caption is empty"}
            </span>
            <span className="text-xs text-slate-500">Emoji quick insert</span>
          </div>

          <EmojiBar onInsert={insertCaptionText} />
        </div>

        <div className="rounded border border-line bg-white p-4 shadow-sm">
          <ButtonBuilder
            buttons={payload.buttons}
            layout={payload.buttonLayout}
            onButtonsChange={(buttons) => updatePayload({ buttons })}
            onLayoutChange={(buttonLayout) => updatePayload({ buttonLayout })}
          />
        </div>
      </section>

      <CardPreview
        mediaType={payload.mediaType}
        file={media}
        title={payload.title}
        caption={payload.caption}
        buttons={payload.buttons}
        layout={payload.buttonLayout}
        aspectRatio={mediaAspectRatio}
        cropPosition={cropPosition}
      />
    </div>
  );
}
